import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { IFilterWithValues } from "../FilterModal";

import { List, ListPerson } from "../../schema/list";
import { isLeadBlacklisted } from "../../utils/isLeadBlacklisted";

/**
 * What a reach-out flow (menu + campaign picker + AI panel) operates on. Either a single lead — the
 * per-lead Outreach column button — or a bulk table selection — the footer "Reach out" action. Both
 * share the same UI; only the commit target differs.
 */
export type ReachOutTarget = ReachOutLeadTarget | ReachOutBulkTarget;

/** A single lead (the Outreach column). */
export interface ReachOutLeadTarget {
	kind: "lead";
	row: ListPerson;
}

/**
 * A bulk table selection (the floating footer bar). `selectedItems` are the ids selected on the
 * current page; for a `SelectType.ALL` selection the backend uses the first of them to resolve the
 * list and then expands to every lead matching `filter` + the status filters (select-all across
 * pages). `count` is what the action will affect (current-page length or the filtered total).
 */
export interface ReachOutBulkTarget {
	kind: "bulk";
	listId: number;
	list: List;
	selectedItems: number[];
	selectType: SelectType;
	count: number;
	/** A representative loaded row — seeds the AI panel's display defaults (language / senders). */
	seedRow: ListPerson;
	/**
	 * The loaded rows of the selection. The blacklist gate reads them (PREC-2548): it can only ask
	 * about leads it can see, so a select-all is confirmed for the loaded page.
	 */
	selectedRows: ListPerson[];
	filter?: IFilterWithValues;
	inclusionFilterStatus?: InclusionFilterStatus[];
	priorityFilterStatus?: PriorityFilterStatus[];
	signalFilterStatus?: SignalFilterStatus[];
	assignStatus?: AssignStatus;
	/** The table's search term: a select-all covers its matches only (PREC-4441). */
	searchValue?: string;
	/** Every status of the tab the table showed; the Assigned tab stands for several (PREC-4450). */
	assignStatuses?: AssignStatus[];
	/** The reason sub-filters the table showed, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons?: string[];
}

/**
 * The selection a bulk request re-derives on the server: for a select-all, the table's whole view —
 * its filters, search, tab statuses, reason sub-filters and signal filter (PREC-4441, PREC-4450).
 * One copy for every assign and generate path, so none of them can leave a part behind.
 */
export const reachOutBulkScope = (target: ReachOutBulkTarget) => ({
	selectType: target.selectType,
	filter: target.filter,
	inclusionFilterStatus: target.inclusionFilterStatus,
	priorityFilterStatus: target.priorityFilterStatus,
	signalFilterStatus: target.signalFilterStatus,
	searchValue: target.searchValue,
	assignStatuses: target.assignStatuses,
	exclusionReasons: target.exclusionReasons,
});

/** The list a reach-out target belongs to (carries `campaignConfig`, `columns`, `campaignStatus`). */
export const reachOutTargetList = (target: ReachOutTarget): List | undefined =>
	target.kind === "lead" ? target.row.list : target.list;

/** The list id a reach-out target belongs to. */
export const reachOutTargetListId = (target: ReachOutTarget): number =>
	target.kind === "lead" ? (target.row.list?.id ?? 0) : target.listId;

/** How many leads the action affects (1 for a single lead, the selection count for bulk). */
export const reachOutTargetCount = (target: ReachOutTarget): number =>
	target.kind === "lead" ? 1 : target.count;

/** The loaded leads a reach-out target would contact. */
export const reachOutTargetLeads = (target: ReachOutTarget): ListPerson[] =>
	target.kind === "lead" ? [target.row] : target.selectedRows;

const holdsBlacklistedLead = (target: ReachOutBulkTarget): boolean =>
	target.selectedRows.some((row) => isLeadBlacklisted(row));

/**
 * The target narrowed to the leads the blacklist gate let through (PREC-2548). A bulk selection
 * that held a blacklisted lead is sent as the explicit ids that are left: a select-all re-derives
 * its leads from the filters, and with them every blacklisted lead the user was never asked about
 * (and, on the Blacklist bucket, would no longer find the ones just taken off it).
 */
export const narrowReachOutTarget = (
	target: ReachOutTarget,
	allowed: ListPerson[]
): ReachOutTarget => {
	if (target.kind === "lead" || !holdsBlacklistedLead(target)) {
		return target;
	}

	return {
		...target,
		selectType: SelectType.CURRENT_PAGE,
		selectedItems: allowed.map((row) => row.id),
		selectedRows: allowed,
		count: allowed.length,
	};
};

/**
 * A select-all reaching past the loaded page that the gate will narrow to it — the modal says so
 * before the user confirms, rather than the send quietly touching fewer leads than were selected.
 */
export const isPageOnlyOverride = (target: ReachOutTarget): target is ReachOutBulkTarget =>
	target.kind === "bulk" &&
	target.selectType === SelectType.ALL &&
	target.count > target.selectedRows.length &&
	holdsBlacklistedLead(target);
